import * as React from 'react';
import { Modal, FormControl, FormGroup, Col, ControlLabel, Alert, Button } from 'react-bootstrap';
import { IState as IStateItem } from '../../../models/index';
import { ICenterAddress } from '@nkr/models/center-address';
import { CenterAddressType } from '@nkr/enums/index';

interface IProps {
    show: boolean,
    centerAddress: ICenterAddress,
    stateList: IStateItem[],
    onSave: (addressLine1: string, addressLine2: string, addressLine3: string, city: string, state: number, postalCode: string, note: string, label: string) => void,
    onCancel: () => void
}

interface IState {
    addressLine1: string,
    addressLine2: string,
    addressLine3: string,
    city: string,
    state: number,
    postalCode: string,
    note: string,
    label: string,
    validation: {
        addressLine1: boolean,
        city: boolean,
        state: boolean,
        postalCode: boolean
    },
    dirty: boolean
}

class CenterAddress extends React.Component<IProps, IState> {
    readonly state: IState = {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        city: '',
        state: 0,
        postalCode: '',
        note: '',
        label: '',
        validation: {
            addressLine1: true,
            city: true,
            state: true,
            postalCode: true
        },
        dirty: false
    }

    componentWillReceiveProps = (nextProps: IProps) => {
        if (nextProps.show && nextProps.centerAddress != null) {
            let { addressLine1, addressLine2, addressLine3, city, stateAbbreviation, postalCode } = nextProps.centerAddress;
            let state = this.props.stateList.find(x => x.abbreviation == stateAbbreviation).id;

            this.setState({
                addressLine1,
                addressLine2,
                addressLine3,
                city,
                state,
                postalCode
            });
        }
    }

    handleItemChange = (value: string, property: string) => {
        let state = { ...this.state };

        state[property] = value;

        this.setState(state);
    }

    handleStateChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            state: parseInt(e.currentTarget.value)
        });
    }

    handleSave = () => {
        let validation = this.validate();

        this.setState({
            validation,
            dirty: true
        }, () => {
            if (this.isValid()) {
                let { addressLine1, addressLine2, addressLine3, city, state, postalCode, note, label } = this.state;
                this.props.onSave(addressLine1, addressLine2, addressLine3, city, state, postalCode, note, label);
            }
        });
    }

    validate = () => {
        let { addressLine1, city, state, postalCode, validation } = this.state;

        validation.addressLine1 = addressLine1 != '';
        validation.city = city != '';
        validation.state = state > 0;
        validation.postalCode = postalCode != '';

        return validation;
    }

    isValid = () => {
        let { addressLine1, city, state, postalCode } = this.state.validation;
        return addressLine1 && city && state && postalCode;
    }

    render = () => {
        const isKidneyPickup = this.props.centerAddress != null ? this.props.centerAddress.typeId == CenterAddressType.KidneyPickup : true;
        const isValid = this.isValid();

        return (
            <Modal show={this.props.show} backdrop='static' onHide={this.props.onCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>{isKidneyPickup ? 'Center Pickup Address Details' : 'Center Delivery Address Details'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.state.dirty && !isValid && (
                        <Alert bsStyle='danger'>
                            The items in red are required and must be completed prior to submitting the address.
                        </Alert>
                    )}

                    <FormGroup validationState={this.state.dirty && !this.state.validation.addressLine1 ? 'error' : null}>
                        <Col lg={4} componentClass={ControlLabel}>
                            Name:
                        </Col>
                        <Col lg={8}>
                            <FormControl
                                value={this.state.label}
                                onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => this.handleItemChange(e.currentTarget.value, 'label')}
                            />
                        </Col>
                    </FormGroup>
                    <FormGroup validationState={this.state.dirty && !this.state.validation.addressLine1 ? 'error' : null}>
                        <Col lg={4} componentClass={ControlLabel}>
                            Address Street/Line 1:
                        </Col>
                        <Col lg={8}>
                            <FormControl
                                value={this.state.addressLine1}
                                onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => this.handleItemChange(e.currentTarget.value, 'addressLine1')}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col lg={4} componentClass={ControlLabel}>
                            Address Street/Line 2:
                        </Col>
                        <Col lg={8}>
                            <FormControl
                                value={this.state.addressLine2}
                                onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => this.handleItemChange(e.currentTarget.value, 'addressLine2')}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col lg={4} componentClass={ControlLabel}>
                            Address Street/Line 3:
                        </Col>
                        <Col lg={8}>
                            <FormControl
                                value={this.state.addressLine3}
                                onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => this.handleItemChange(e.currentTarget.value, 'addressLine3')}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={this.state.dirty && !this.state.validation.city ? 'error' : null}>
                        <Col lg={4} componentClass={ControlLabel}>
                            City:
                        </Col>
                        <Col lg={8}>
                            <FormControl
                                value={this.state.city}
                                onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => this.handleItemChange(e.currentTarget.value, 'city')}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={this.state.dirty && !this.state.validation.state ? 'error' : null}>
                        <Col lg={4} componentClass={ControlLabel}>
                            State/Province:
                        </Col>
                        <Col lg={8}>
                            <FormControl
                                componentClass='select'
                                value={this.state.state}
                                onChange={this.handleStateChange}
                            >
                                <option value={0}>- select -</option>
                                {this.props.stateList.map((state, index) =>
                                    <option key={`state-${index}`} value={state.id}>{state.description}</option>
                                )}
                            </FormControl>
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={this.state.dirty && !this.state.validation.postalCode ? 'error' : null}>
                        <Col lg={4} componentClass={ControlLabel}>
                            Postal Code:
                        </Col>
                        <Col lg={8}>
                            <FormControl
                                value={this.state.postalCode}
                                onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => this.handleItemChange(e.currentTarget.value, 'postalCode')}
                            />
                        </Col>
                    </FormGroup>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle='primary' onClick={this.handleSave}>Save</Button>
                    <Button onClick={this.props.onCancel}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default CenterAddress;